// SPDX-License-Identifier: BUSL-1.1

/**
 * <LiveActivityCell> — per-row preview of the live fleet activity stream
 * (CEO-V4 Phase 2 / L2).
 *
 * Renders the last 1-3 evidence-of-work events for a single agent inside
 * the operators DataTable. Designed to mount cheaply: one fleet SSE
 * connection lives at the route level (see FleetActivityStreamProvider);
 * each cell subscribes to that shared buffer and filters in-memory by
 * `agentId`. This keeps us comfortably under the 10-stream-per-org cap
 * even when the grid holds 100 agents.
 *
 * Color scheme mirrors <LiveStreamPanel> so the moderator builds one
 * mental map of kinds (tool_call=purple, decision=blue, blocker=red,
 * artifact=green, etc.) across the entire console.
 */

import { createContext, useContext } from 'react';
import { API_BASE } from '@/lib/api-client';
import {
  useFleetActivityStream,
  type FleetActivityEvent,
} from '@/hooks/use-fleet-activity-stream';
import { parseActivityPayload } from '@/components/agent-detail/activity-payload-schemas';

/* ── Context ─────────────────────────────────────────────────────────────── */

/**
 * Shared state surfaced by <FleetActivityStreamProvider>. The route mounts
 * the provider once with the current orgId; every <LiveActivityCell> reads
 * the buffer from context and filters by agentId.
 *
 * Exported so other consumers under the same provider (e.g. the drilldown
 * sheet's agent-level live panel and the JobDetailSheet's per-job live
 * event log) can read the same buffer instead of opening another SSE
 * connection.
 */
export interface FleetActivityStreamContextValue {
  events: FleetActivityEvent[];
  connected: boolean;
  error: string | null;
}

export const FleetActivityStreamContext =
  createContext<FleetActivityStreamContextValue | null>(null);

/**
 * Wraps a subtree (typically the operators DataTable) with a single org-wide
 * fleet stream. Children read via <LiveActivityCell>.
 */
export function FleetActivityStreamProvider({
  orgId,
  children,
}: {
  orgId: string | null;
  children: React.ReactNode;
}) {
  const { events, connected, error } = useFleetActivityStream(
    orgId,
    API_BASE,
  );
  return (
    <FleetActivityStreamContext.Provider
      value={{ events, connected, error }}
    >
      {children}
    </FleetActivityStreamContext.Provider>
  );
}

/* ── Status banner ───────────────────────────────────────────────────────── */

/**
 * Fleet-level stream status pill. Reads from the shared
 * <FleetActivityStreamProvider> context — render this ABOVE the operators
 * DataTable, inside the provider, so operators see a clear top-of-grid
 * signal when the stream drops.
 *
 * Pre-this-PR, `<LiveActivityCell>` showed "offline" per-agent only AFTER
 * an agent had emitted events the cell was tracking. A fresh disconnect
 * with no buffered events looked indistinguishable from a quiet fleet.
 *
 * Four states, in priority order:
 *   - error          → red, "Stream offline — events may be stale" + detail tooltip
 *   - disconnected   → amber, "Reconnecting…" (happens between backoff retries)
 *   - connecting     → blue, "Connecting…" (initial mount, no events yet)
 *   - connected      → muted green dot + "Live" (steady state)
 */
export function FleetActivityStreamStatusBanner() {
  const ctx = useContext(FleetActivityStreamContext);
  if (!ctx) return null; // banner only renders inside a provider; safe no-op
  const { connected, events, error } = ctx;

  if (error) {
    return (
      <div
        className="mb-3 flex items-center gap-2 rounded border border-red-900/50 bg-red-950/30 px-3 py-1.5 text-xs"
        role="status"
        aria-live="polite"
        title={error}
      >
        <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-red-500" />
        <span className="text-red-300">Stream offline — events may be stale.</span>
        <span className="ml-auto truncate text-red-500/70">{error}</span>
      </div>
    );
  }

  if (!connected) {
    // No events buffered yet AND not connected → initial connect. With
    // events present AND not connected → reconnecting after a drop.
    const isInitial = events.length === 0;
    return (
      <div
        className={`mb-3 flex items-center gap-2 rounded border px-3 py-1.5 text-xs ${
          isInitial
            ? 'border-blue-900/50 bg-blue-950/30 text-blue-300'
            : 'border-amber-900/50 bg-amber-950/30 text-amber-300'
        }`}
        role="status"
        aria-live="polite"
      >
        <span
          className={`inline-block h-2 w-2 shrink-0 animate-pulse rounded-full ${
            isInitial ? 'bg-blue-400' : 'bg-amber-400'
          }`}
        />
        <span>{isInitial ? 'Connecting…' : 'Reconnecting…'}</span>
      </div>
    );
  }

  return (
    <div
      className="mb-3 flex items-center gap-2 text-xs text-neutral-500"
      role="status"
      aria-live="polite"
    >
      <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-green-500" />
      <span>Live</span>
    </div>
  );
}

/* ── Blocker banner ──────────────────────────────────────────────────────── */

/**
 * Window for "recent" blockers. Blockers are EMIT-only — there's no
 * `resolved_at` column, so we approximate "currently unresolved" with
 * "emitted in the last hour". Operators decide when to act.
 */
const BLOCKER_RECENT_MS = 60 * 60 * 1000;

/**
 * Top-of-grid pill that surfaces every agent with a recent `kind='blocker'`
 * activity event in the rolling stream buffer. Clicking an agent chip lifts
 * to the parent route via `onSelectAgent` → opens the drilldown for that
 * agent (where operators can review the blocker in full context).
 *
 * This is the only fleet-level signal for in-progress blockers today.
 * Per-agent `<LiveActivityCell>` only renders blockers if an agent has
 * recent buffered events; without an explicit pull, an operator scanning
 * the grid could miss a stalled agent. The banner fills that gap.
 */
export function FleetBlockerBanner({
  onSelectAgent,
}: {
  onSelectAgent?: (agentId: string) => void;
}) {
  const ctx = useContext(FleetActivityStreamContext);
  if (!ctx) return null;
  const cutoff = Date.now() - BLOCKER_RECENT_MS;
  const blockerAgents = new Set<string>();
  for (const ev of ctx.events) {
    if (ev.kind !== 'blocker') continue;
    if (Date.parse(ev.occurredAt) < cutoff) continue;
    blockerAgents.add(ev.agentId);
  }
  if (blockerAgents.size === 0) return null;

  const agentIds = Array.from(blockerAgents);
  const visible = agentIds.slice(0, 5);
  const overflow = agentIds.length - visible.length;

  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-2 rounded border border-red-900/50 bg-red-950/30 px-3 py-1.5 text-xs"
      role="status"
      aria-live="polite"
    >
      <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-red-500" />
      <span className="font-medium text-red-300">
        {agentIds.length} {agentIds.length === 1 ? 'agent has' : 'agents have'} recent blockers
      </span>
      <span className="text-red-500/70">·</span>
      <div className="flex flex-wrap items-center gap-1">
        {visible.map((agentId) => (
          <button
            type="button"
            key={agentId}
            onClick={() => onSelectAgent?.(agentId)}
            className="font-mono text-red-300 underline-offset-2 hover:text-red-200 hover:underline focus:outline-none focus:ring-1 focus:ring-red-500 rounded"
            aria-label={`Open drilldown for ${agentId}`}
          >
            {agentId}
          </button>
        ))}
        {overflow > 0 && (
          <span className="text-red-500/70">+{overflow} more</span>
        )}
      </div>
    </div>
  );
}

/* ── Cell ────────────────────────────────────────────────────────────────── */

/** Cap on events shown inline per cell — keeps the row at constant height. */
const MAX_VISIBLE = 3;

/** Cell width cap — keeps the DataTable column at ~280px. */
const CELL_WIDTH_CLASS = 'max-w-[280px]';

/** Event-kind to tone mapping. Mirrors live-stream-panel.tsx semantics. */
const KIND_COLORS: Record<string, string> = {
  tool_call: 'bg-purple-900/30 text-purple-300',
  artifact: 'bg-green-900/30 text-green-300',
  decision: 'bg-blue-900/30 text-blue-300',
  context_compressed: 'bg-amber-900/30 text-amber-300',
  blocker: 'bg-red-900/30 text-red-300',
  task_started: 'bg-cyan-900/30 text-cyan-300',
  task_completed: 'bg-emerald-900/30 text-emerald-300',
};

function KindBadge({ kind }: { kind: string }) {
  const tone = KIND_COLORS[kind] ?? 'bg-neutral-700 text-neutral-300';
  return (
    <span
      className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${tone}`}
    >
      {kind}
    </span>
  );
}

function buildEventPreview(event: FleetActivityEvent): string {
  // Type-safe preview via the shared Zod schemas. Each kind branch reads
  // only fields the schema guarantees. Schema drift → 'unknown' branch
  // returns truncated JSON (and logs via parseActivityPayload).
  const parsed = parseActivityPayload(event.kind, event.payload ?? {});
  switch (parsed.kind) {
    case 'tool_call':
      return parsed.data.tool;
    case 'artifact':
      return parsed.data.title ?? parsed.data.uri;
    case 'decision':
      return parsed.data.summary;
    case 'context_compressed':
      return `${parsed.data.before_tokens} → ${parsed.data.after_tokens} tok`;
    case 'blocker':
      // stall_suspected = auto-fired by HB-04 watchdog, not agent-reported.
      // The 🤖 prefix tells operators it's a "went silent" signal, not an
      // "I reported an issue" signal — different remediation path.
      return parsed.data.kind === 'stall_suspected'
        ? `🤖 ${parsed.data.detail}`
        : parsed.data.detail;
    case 'task_started':
      return parsed.data.title ?? parsed.data.task_id;
    case 'task_completed':
      return parsed.data.outcome ?? parsed.data.task_id;
    case 'unknown':
      return JSON.stringify(parsed.raw);
  }
}

function EventLine({ event }: { event: FleetActivityEvent }) {
  let preview = buildEventPreview(event);
  if (preview.length > 60) preview = preview.slice(0, 60) + '…';

  return (
    <div
      className="flex items-center gap-1.5 truncate text-xs"
      title={`${event.kind} — ${event.occurredAt}`}
    >
      <KindBadge kind={event.kind} />
      <span className="truncate font-mono text-neutral-400">{preview}</span>
    </div>
  );
}

/**
 * Renders the latest 1-3 activity events for a single agent. Reads from
 * the shared fleet-stream context if present (the canonical wiring) and
 * falls back to no-op rendering otherwise — so the cell stays safe if a
 * caller forgets to wrap with <FleetActivityStreamProvider>.
 */
export function LiveActivityCell({ agentId }: { agentId: string }) {
  const ctx = useContext(FleetActivityStreamContext);
  if (!ctx) {
    return <span className="text-xs text-neutral-600">—</span>;
  }
  const { events, connected, error } = ctx;
  // Filter latest-first so we can show "newest at top" in 3 rows.
  const mine = events
    .filter((e) => e.agentId === agentId)
    .slice(-MAX_VISIBLE)
    .reverse();

  if (mine.length === 0) {
    return (
      <div className={`${CELL_WIDTH_CLASS} text-xs text-neutral-500`}>
        {connected
          ? 'waiting…'
          : error
            ? <span className="text-red-400" title={error}>offline</span>
            : 'idle'}
      </div>
    );
  }

  return (
    <div className={`${CELL_WIDTH_CLASS} space-y-1 overflow-hidden`}>
      {mine.map((event) => (
        <EventLine key={event.id} event={event} />
      ))}
    </div>
  );
}
